import {Button, Stack, Typography, useTheme} from '@mui/material';
import {DateView, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {ptBR} from 'date-fns/locale';
import {Modal} from 'presentation/components';
import {QueryParamsEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {useQueryParams} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {formatText, stringfyDate} from 'presentation/utils';
import React, {useState, useEffect} from 'react';
import {getCurrentYear} from 'presentation/utils/getCurrentYear';
import {useLocation} from 'react-router-dom';
import {ServicesTaggingEnum} from 'main/analytics/gaTagsEnum';
import {StyledDateCalendar} from './styles';
import {DateCalendarModalProps, MonthAndYearState} from './types';

const {apply, clear} = translations['pt-br'].commons;

export function MonthYearPickerCalendar({
	open,
	setOpen,
	handleSecondaryAction,
	headerTitle,
	viewMode = 'month-year',
	maxDate,
	minDate,
	shouldMaxDateBeDefault = false,
}: DateCalendarModalProps) {
	const {colors} = useTheme();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();

	const {pathname} = useLocation();
	const isIncomeTaxPath = pathname.includes('imposto-de-renda');

	const {month: monthParam, year: yearParam} = SEARCH_PARAMS;
	const IS_YEAR = viewMode === 'year';
	const DEFAULT_VIEW_MODE = IS_YEAR ? 'year' : 'month';
	const MONTH_OR_YEAR_PARAMS_EXISTS = monthParam.length > 0 || yearParam.length > 0;

	const currentDate = new Date();

	const currentMonth = currentDate.getMonth();
	const currentYear = new Date(
		getCurrentYear({year: currentDate.getFullYear().toString(), maxDate, minDate}),
		currentMonth,
	);

	const [monthAndYearState, setMonthAndYearState] = useState<MonthAndYearState>(
		MONTH_OR_YEAR_PARAMS_EXISTS
			? {
					month: !monthParam.length ? '01' : monthParam,
					year: getCurrentYear({
						year:
							yearParam.length >= 4
								? yearParam
								: currentDate.getFullYear().toString(),
						maxDate,
						minDate,
					}),
				}
			: undefined,
	);

	const [viewModeState, setViewModeState] = useState<DateView>(DEFAULT_VIEW_MODE);
	const initialTitle = monthAndYearState
		? new Date(Number(monthAndYearState.year), Number(monthAndYearState.month) - 1)
		: currentYear;

	const [headerTitleState, setHeaderTitleState] = useState(
		stringfyDate(initialTitle, 'MMMM yyyy'),
	);

	const handleClose = () => {
		if (handleSecondaryAction) {
			handleSecondaryAction();
		}
		setViewModeState(DEFAULT_VIEW_MODE);
		setOpen(false);
	};

	const handleClearDateFilter = () => {
		setSearchParams(
			prev => {
				prev.delete('month');
				prev.delete('year');
				return prev;
			},
			{replace: true},
		);
		extractMonthAndYear(
			shouldMaxDateBeDefault && maxDate ? maxDate?.toISOString() : new Date().toISOString(),
		);
		handleClose();
	};

	const extractMonthAndYear = (dateString: string): MonthAndYearState => {
		const date = new Date(dateString);
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear();
		const state = {month, year};
		setMonthAndYearState(state);
		renderTitleHeader(state);
		return state;
	};

	const updateSearchParams = () => {
		setSearchParams(prev => {
			if (monthAndYearState) {
				if (viewMode === 'month-year') {
					prev.set(QueryParamsEnum.MONTH, monthAndYearState.month);
				}
				prev.set(QueryParamsEnum.YEAR, monthAndYearState.year.toString());
			}
			return prev;
		});
		handleClose();
	};

	const renderTitleHeader = (selectedDate: MonthAndYearState) => {
		if (selectedDate) {
			const {month, year} = selectedDate;
			const date = new Date(year, Number(month) - 1);
			setHeaderTitleState(stringfyDate(date, 'MMMM yyyy'));
			return;
		}
		setHeaderTitleState(stringfyDate(currentYear, 'MMMM yyyy'));
	};

	const isDisabled = !(monthAndYearState?.year && (IS_YEAR || monthAndYearState?.month));

	useEffect(() => {
		if (open && isIncomeTaxPath) {
			const timer = setTimeout(() => {
				const yearButtons = document.querySelectorAll('.MuiPickersYear-root button');
				yearButtons.forEach(button => {
					const yearText = button.textContent;
					if (yearText) {
						button.setAttribute(
							'data-tag-id',
							`${ServicesTaggingEnum.CLICK_IR_ANO}${yearText}`,
						);
					}
				});
			}, 100);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [open, viewModeState, isIncomeTaxPath]);

	return (
		<Modal.Root
			title={headerTitle}
			open={open}
			onClose={handleClose}
			customMaxWidth={376}
			sx={{margin: 'auto'}}>
			<Modal.Header color="primary" onClose={handleClose} title={headerTitle} />
			<Modal.Content sx={{p: {xs: '16px', sm: '24px'}}}>
				<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
					<StyledDateCalendar
						yearsPerRow={4}
						monthsPerRow={4}
						slots={{
							calendarHeader: () =>
								IS_YEAR ? (
									<Typography fontSize={'1rem'} fontWeight={600}>
										{monthAndYearState?.year ??
											stringfyDate(currentYear, 'yyyy')}
									</Typography>
								) : (
									<Stack>
										<Button
											onClick={() =>
												setViewModeState(state =>
													state === 'month' ? 'year' : 'month',
												)
											}
											variant="text"
											sx={{
												width: 'fit-content',
												color: theme => theme.colors.light.text.primary,
												fontSize: '1rem',
												fontWeight: 600,
											}}>
											{formatText(headerTitleState)}
											<Icon
												name="arrow-drop-down"
												color={colors.gray['54Percent']}
												style={{
													transition: 'all 200ms ease-in-out',
													rotate:
														viewModeState === 'year'
															? '180deg'
															: '0deg',
												}}
											/>
										</Button>
									</Stack>
								),
						}}
						value={
							monthAndYearState
								? new Date(
										Number(monthAndYearState.year),
										Number(monthAndYearState.month) - 1,
									)
								: currentYear
						}
						views={IS_YEAR ? ['year'] : [viewModeState]}
						openTo={IS_YEAR ? 'year' : 'month'}
						defaultValue={currentYear}
						maxDate={maxDate}
						minDate={minDate}
						onChange={date => {
							if (date) {
								extractMonthAndYear(date.toISOString());
							}
						}}
					/>
				</LocalizationProvider>
			</Modal.Content>
			<Modal.Footer>
				<Button
					data-tag-id={
						isIncomeTaxPath ? ServicesTaggingEnum.CLICK_LIMPAR_FILTRO_IR : null
					}
					variant="outlined"
					size="large"
					fullWidth
					onClick={handleClearDateFilter}>
					{clear}
				</Button>
				<Button
					data-tag-id={
						isIncomeTaxPath ? ServicesTaggingEnum.CLICK_APLICAR_FILTRO_IR : null
					}
					variant="contained"
					size="large"
					fullWidth
					onClick={updateSearchParams}
					disabled={isDisabled}>
					{apply}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
}
